/**
 * The feedback drawer.
 *
 * Opens over the workspace the person is evaluating; they never leave it.
 * Four things to answer, one button. Everything else — who, which workspace,
 * which view, which build, which device, which facility — the platform
 * already knows and records for them. Nothing from the page itself is
 * captured: no screenshot, no form values, no URL.
 *
 * Under the form, "My feedback": what they sent and what became of it, in
 * plain words. Feedback that disappears into a black hole trains people to
 * stop giving it.
 *
 * A true modal, on the shadcn Sheet (Radix Dialog): focus is trapped and moves to the first field on open, Escape and
 * the scrim close it, focus goes back to the door that opened it, and everything behind is inert. The panel keeps its
 * own look (.fb-drawer); the Sheet supplies the behaviour.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  feedbackApi, deviceClass,
  EXTERNAL_TOOLS, EXTERNAL_TOOL_LABEL, STATUS_LABEL,
  ExternalTool, MyFeedbackItem, PromptSource, Workspace,
} from '../../services/feedbackApi';
import { Sheet, SheetDescription, SheetOverlay, SheetPanel, SheetPortal, SheetTitle } from '../shadcn/ui/sheet';
import { useReturnFocus } from '../shadcn/ui/dialog';
import './feedback.css';

export interface FeedbackContext {
  workspace: Workspace;
  /** A route pattern, never a concrete URL: '/provider/:facilityId'. */
  routeTemplate: string;
  viewName: string;
  facilityId: string | null;
}

interface Props extends FeedbackContext {
  open: boolean;
  onClose: () => void;
  promptSource: PromptSource;
}

/** The quiet door. Text, not a coloured button. */
export function FeedbackButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="fb-trigger" onClick={onClick}>Feedback</button>
  );
}

function shortDay(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function truncate(s: string, n: number): string {
  const t = s.trim();
  return t.length <= n ? t : `${t.slice(0, n - 1).trimEnd()}…`;
}

function MyFeedback({ items, failed }: { items: MyFeedbackItem[] | null; failed: boolean }) {
  return (
    <section className="fb-mine" aria-labelledby="fb-mine-title">
      <h3 id="fb-mine-title">My feedback</h3>
      {failed && <p className="fb-empty">Could not load what you have sent before.</p>}
      {!failed && items && items.length === 0 && (
        <p className="fb-empty">Nothing yet. What you send shows up here with what happened to it.</p>
      )}
      {!failed && items && items.length > 0 && (
        <ul>
          {items.map((it) => {
            const when = it.resolved_at || it.created_at;
            return (
              <li key={it.id}>
                <span className="fb-item-title">{truncate(it.task_attempted, 90)}</span>
                <span className="fb-item-meta">
                  <span className={`fb-status is-${it.status.toLowerCase()}`}>{STATUS_LABEL[it.status]}</span>
                  <span>· {shortDay(when)}</span>
                </span>
                {it.resolution_note && <p className="fb-item-note">{it.resolution_note}</p>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default function FeedbackDrawer({
  open, onClose, promptSource, workspace, routeTemplate, viewName, facilityId,
}: Props) {
  const [task, setTask] = useState('');
  const [happened, setHappened] = useState('');
  const [easier, setEasier] = useState('');
  const [left, setLeft] = useState<'no' | 'yes'>('no');
  const [tool, setTool] = useState<ExternalTool | ''>('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mine, setMine] = useState<MyFeedbackItem[] | null>(null);
  const [mineFailed, setMineFailed] = useState(false);

  const firstField = useRef<HTMLTextAreaElement>(null);
  const returnFocus = useReturnFocus(open);

  const loadMine = useCallback(async () => {
    try {
      const res = await feedbackApi.mine();
      if (res.success && res.data) { setMine(res.data); setMineFailed(false); } else setMineFailed(true);
    } catch {
      setMineFailed(true);
    }
  }, []);

  // Each opening starts clean and reads what the person sent before. (Focus in, Escape, focus back to the door: Radix.)
  useEffect(() => {
    if (!open) return;
    setSent(false);
    setError(null);
    loadMine();
  }, [open, loadMine]);

  const reset = () => { setTask(''); setHappened(''); setEasier(''); setLeft('no'); setTool(''); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!task.trim() || !happened.trim()) {
      setError('Say what you were trying to do and what happened. The third question is optional.');
      return;
    }
    if (left === 'yes' && !tool) {
      setError('Say where you went to finish the task.');
      return;
    }
    setSending(true);
    try {
      const res = await feedbackApi.submit({
        workspace,
        route_template: routeTemplate,
        view_name: viewName,
        facility_id: facilityId,
        device_class: deviceClass(window.innerWidth),
        viewport_width: window.innerWidth,
        prompt_source: promptSource,
        task_attempted: task.trim(),
        observed_friction: happened.trim(),
        desired_outcome: easier.trim() || null,
        left_platform: left === 'yes',
        external_tool: left === 'yes' ? (tool as ExternalTool) : null,
      });
      if (res.success) {
        setSent(true);
        reset();
        loadMine();
      } else {
        setError(res.error || res.message || 'Could not send this. Try again in a moment.');
      }
    } catch {
      setError('Could not reach the server. Nothing was sent; try again in a moment.');
    } finally {
      setSending(false);
    }
  };

  // Unmounted the moment it closes (see Drawer in pages/admin/bd/bdUi.tsx): no exit pass to trap focus.
  if (!open) return null;

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetPortal>
      <div className="fb-root">
      <SheetOverlay className="z-40 bg-[rgba(16,37,45,0.28)]" />
      <SheetPanel
        className="fb-drawer"
        onOpenAutoFocus={(e) => { e.preventDefault(); firstField.current?.focus(); }}
        onCloseAutoFocus={returnFocus}
      >
        <header className="fb-head">
          <SheetTitle>Feedback</SheetTitle>
          <SheetDescription className="sr-only">Tell the eMerge team what got in your way.</SheetDescription>
          <button type="button" className="fb-close" onClick={onClose} aria-label="Close feedback">×</button>
        </header>

        <div className="fb-body">
          {sent ? (
            <div className="fb-sent" role="status">
              <strong>Sent. Thank you.</strong>
              <p>The team reads every one. You will see what happens to it under My feedback.</p>
              <button type="button" className="fb-again" onClick={() => setSent(false)}>Send another</button>
            </div>
          ) : (
            <>
              <p className="fb-lede">
                Tell the eMerge team what got in your way. You will see what happens to it here.
              </p>
              <form className="fb-form" onSubmit={submit} noValidate>
                <label className="fb-field">
                  <span>What were you trying to do?</span>
                  <textarea ref={firstField} rows={2} maxLength={2000} value={task} onChange={(e) => setTask(e.target.value)} required />
                </label>
                <label className="fb-field">
                  <span>What happened?</span>
                  <textarea rows={3} maxLength={4000} value={happened} onChange={(e) => setHappened(e.target.value)} required />
                </label>
                <label className="fb-field">
                  <span>What would have made this easier?<span className="fb-opt">optional</span></span>
                  <textarea rows={2} maxLength={2000} value={easier} onChange={(e) => setEasier(e.target.value)} />
                </label>

                <fieldset className="fb-choice">
                  <legend className="fb-legend">Did you leave eMerge to finish the task?</legend>
                  <div className="fb-radios">
                    <label>
                      <input type="radio" name="fb-left" value="no" checked={left === 'no'} onChange={() => { setLeft('no'); setTool(''); }} />
                      No
                    </label>
                    <label>
                      <input type="radio" name="fb-left" value="yes" checked={left === 'yes'} onChange={() => setLeft('yes')} />
                      Yes
                    </label>
                  </div>
                  {left === 'yes' && (
                    <select aria-label="Where did you go?" value={tool} onChange={(e) => setTool(e.target.value as ExternalTool | '')}>
                      <option value="">Where did you go?</option>
                      {EXTERNAL_TOOLS.map((t) => <option key={t} value={t}>{EXTERNAL_TOOL_LABEL[t]}</option>)}
                    </select>
                  )}
                </fieldset>

                <p className="fb-auto">
                  We record where you were in eMerge, the facility you had open, your device type and the
                  build you were using. Nothing on the page itself is captured.
                </p>

                {error && <p className="fb-error" role="alert">{error}</p>}

                <button type="submit" className="fb-send" disabled={sending}>
                  {sending ? 'Sending…' : 'Send feedback'}
                </button>
              </form>
            </>
          )}

          <MyFeedback items={mine} failed={mineFailed} />
        </div>
      </SheetPanel>
      </div>
      </SheetPortal>
    </Sheet>
  );
}
